import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import PlatFormAPIAxiosInstance from "../../../shared/utils/platFormAxios";
import { useTrialsRouter } from "../../../shared/custom-hooks/useTrialsRouter";

export interface SeedCatalogProduct {
  id: string;
  commercial_name: string;
  brand?: string;
  manufacturer?: string;
}

export interface SeedCropDetails {
  crop_id?: string;
  localized_crop_ids?: string[];
  country?: string;
}

interface CatalogPage<T> {
  content?: T[];
  last?: boolean;
}

interface CatalogCrop {
  id: string;
  localized_crops?: Array<{
    id: string;
    location?: string;
  }>;
}

const MAX_PAGES = 100;

const querySeeds = async (
  cropIds: string[],
  signal: AbortSignal
): Promise<SeedCatalogProduct[]> => {
  if (cropIds.length === 0) {
    return [];
  }

  const products: SeedCatalogProduct[] = [];

  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const response = await PlatFormAPIAxiosInstance.post<
      CatalogPage<SeedCatalogProduct>
    >(
      `/catalog/seeds/query?page=${page}&size=1000`,
      {
        localized_crop_id: cropIds,
        show_canonical: true,
      },
      { signal }
    );

    products.push(...(response.data.content ?? []));

    if (response.data.last ?? true) {
      return Array.from(
        new Map(products.map((product) => [product.id, product])).values()
      );
    }
  }

  throw new Error("The seed catalog exceeded the pagination limit.");
};

const resolveLocalizedCropIds = async (
  cropId: string | undefined,
  country: string | undefined,
  signal: AbortSignal
): Promise<string[]> => {
  if (!cropId || !country) {
    return [];
  }

  const crops: CatalogCrop[] = [];

  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const response = await PlatFormAPIAxiosInstance.get<
      CatalogPage<CatalogCrop>
    >(
      `catalog/crops?include_deleted_crop=false&country=${encodeURIComponent(
        country
      )}&page=${page}&size=200`,
      { signal }
    );

    crops.push(...(response.data.content ?? []));

    if (response.data.last ?? true) {
      const matchingCrop = crops.find(
        (crop) =>
          crop.id === cropId ||
          crop.localized_crops?.some((entry) => entry.id === cropId)
      );

      const localizedCrop = matchingCrop?.localized_crops?.find(
        (entry) => entry.location?.toUpperCase() === country.toUpperCase()
      );

      return localizedCrop ? [localizedCrop.id] : [];
    }
  }

  throw new Error("The crop catalog exceeded the pagination limit.");
};

const useSeedCatalog = (
  protocolId: string | undefined,
  cropDetails: SeedCropDetails | undefined
): UseQueryResult<SeedCatalogProduct[], Error> => {
  const { orgId, workspaceId } = useTrialsRouter();

  const storedCropIds = Array.from(
    new Set(
      (cropDetails?.localized_crop_ids?.length
        ? cropDetails.localized_crop_ids
        : [cropDetails?.crop_id]
      ).filter(
        (id): id is string => typeof id === "string" && id.trim().length > 0
      )
    )
  );

  return useQuery<SeedCatalogProduct[], Error>({
    queryKey: [
      "ratesDosageSeedCatalog",
      orgId ?? "",
      workspaceId ?? "",
      protocolId,
      cropDetails?.country ?? "",
      cropDetails?.crop_id ?? "",
      storedCropIds,
    ],
    enabled: Boolean(protocolId && cropDetails),
    queryFn: async ({ signal }): Promise<SeedCatalogProduct[]> => {
      const products = await querySeeds(storedCropIds, signal);

      if (products.length > 0) {
        return products;
      }

      const resolvedIds = await resolveLocalizedCropIds(
        cropDetails?.crop_id,
        cropDetails?.country,
        signal
      );

      const newIds = resolvedIds.filter((id) => !storedCropIds.includes(id));

      return newIds.length > 0 ? await querySeeds(newIds, signal) : [];
    },
    retry: false,
  });
};

export default useSeedCatalog;
